import { GridStateColDef } from '@mui/x-data-grid-pro/internals';
import { AssessmentMethod, AssessmentWithEntries } from '@schooly/api';

import {
  assessmentMethodTypes,
  getMarkbookForGroupAssessmentParams,
  getMarkbookForGroupsAssessmentEntries,
} from './assessmentEntryUpdates';
import { parseMarkbookForGroupFieldKey } from './assessmentFieldKeys';
import {
  isMarkbookForGroupsStudentRow,
  isStudentsColumn,
  MarkbookForGroupsColumnData,
  MarkbookForGroupsRow,
} from './types';
import { hasMultipleGroupsEntriesInCell } from './utils';

type AssessmentPermissionContext = {
  method: AssessmentMethod;
  enterableAndMyGroup: boolean;
  permissions: string[];
};

type AssessmentCellEditabilityParams = {
  field: string;
  studentId: string;
  assessments: AssessmentWithEntries[];
  permissions: string[];
};

export const canEditScoreOrGradeCell = ({
  method,
  enterableAndMyGroup,
  permissions,
}: AssessmentPermissionContext): boolean => {
  return Boolean(method.id && (permissions.includes('assessment_manager') || enterableAndMyGroup));
};

export const canEditCommentCell = ({
  method,
  enterableAndMyGroup,
}: Omit<AssessmentPermissionContext, 'permissions' | 'field'>): boolean => {
  return Boolean(method.id && enterableAndMyGroup);
};

export const isMarkbookForGroupCellEditable = ({
  field,
  studentId,
  assessments,
  permissions,
}: AssessmentCellEditabilityParams): boolean => {
  if (isStudentsColumn(field)) return false;

  const fieldComponents = parseMarkbookForGroupFieldKey(field);
  if (!fieldComponents) return false;

  const { methodType, assessmentIndex, methodIndex } = fieldComponents;

  const assessment = assessments[assessmentIndex];
  const method = assessment?.methods[methodIndex];

  if (!assessment || !method) return false;

  const params = getMarkbookForGroupAssessmentParams(studentId, method, assessment);
  const isEmpty = !params;

  if (isEmpty) return false;

  const context: AssessmentPermissionContext = {
    method,
    enterableAndMyGroup: assessment.enterable_and_my_group,
    permissions,
  };

  if (assessmentMethodTypes.isScoreOrGrade(methodType)) {
    return canEditScoreOrGradeCell(context);
  }

  const hasComments = params.entries.length > 0;
  return hasComments || canEditCommentCell(context);
};

export const createMarkbookForGroupPermissionChecker = (
  assessments: AssessmentWithEntries[],
  permissions: string[],
) => {
  return {
    isCellEditable: (studentId: string, field: string): boolean => {
      return isMarkbookForGroupCellEditable({
        field,
        studentId,
        assessments,
        permissions,
      });
    },
  };
};

type IsMarkbookForGroupsCellEditableParams = {
  permissions: string[];
  row: MarkbookForGroupsRow;
  column: GridStateColDef & { data: MarkbookForGroupsColumnData };
};

export type MarkbookForGroupsCellEditableProps = Omit<
  IsMarkbookForGroupsCellEditableParams,
  'permissions'
>;

export const createMarkbookForGroupsPermissionChecker = (permissions: string[]) => {
  return {
    isCellEditable: (props: MarkbookForGroupsCellEditableProps) => {
      return isMarkbookForGroupsCellEditable({
        ...props,
        permissions,
      });
    },
  };
};

export const isMarkbookForGroupsCellEditable = ({
  permissions,
  row,
  column,
}: IsMarkbookForGroupsCellEditableParams): boolean => {
  if (isStudentsColumn(column.field)) return false;
  if (!isMarkbookForGroupsStudentRow(row)) return false;
  if (!column.data) return false;

  const { assessmentEntries } = row.data;
  const { assessmentId, method, subject } = column.data;

  if (!method.id) return false;

  const entries = getMarkbookForGroupsAssessmentEntries({
    assessmentId,
    groupIds: subject.groups.map((item) => item.id),
    methodId: method.id,
    assessmentEntries,
  });

  const isEmpty = !entries.length;
  if (isEmpty) return false;

  const hasMultipleGroupsEntries = hasMultipleGroupsEntriesInCell(entries);
  if (hasMultipleGroupsEntries) return false;

  const context: AssessmentPermissionContext = {
    method,
    enterableAndMyGroup: entries[0].enterable_and_my_group ?? false,
    permissions,
  };

  if (assessmentMethodTypes.isScoreOrGrade(method.method_type)) {
    return canEditScoreOrGradeCell(context);
  }

  const hasComments = entries.some((item) => item.comment);
  return hasComments || canEditCommentCell(context);
};
